if (!customElements.get('pp-cart-cross-sell')) {
  // Recommendations API caps complementary products at 10 per product.
  const RECOMMENDATIONS_LIMIT = 10;
  const MAX_TRIGGER_PRODUCTS = 5;

  // Keyed by cart products + limit: the drawer is re-rendered on every cart change, so a
  // quantity update reuses the previous result instead of refetching.
  const resultsCache = new Map();

  document.addEventListener('change', (event) => {
    const select = event.target.closest('.pp-cart-cross-sell__variant-select');
    if (!select) return;

    const card = select.closest('[data-cross-sell-card]');
    const selectedOption = select.options[select.selectedIndex];
    const price = card && card.querySelector('[data-cross-sell-price]');
    const comparePrice = card && card.querySelector('[data-cross-sell-compare-price]');

    if (price && selectedOption.dataset.price) {
      price.textContent = selectedOption.dataset.price;
    }

    if (comparePrice) {
      const comparePriceValue = selectedOption.dataset.comparePrice || '';
      comparePrice.textContent = comparePriceValue;
      comparePrice.classList.toggle('hidden', comparePriceValue === '');
    }
  });

  class PpCartCrossSell extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-cross-sell-track]');
      this.render();
    }

    async render() {
      let productIds = [];
      try {
        productIds = JSON.parse(this.dataset.productIds || '[]').map(String);
      } catch (error) {
        productIds = [];
      }
      const limit = parseInt(this.dataset.limit, 10) || 3;
      const cacheKey = `${productIds.join(',')}|${limit}`;

      if (!resultsCache.has(cacheKey)) {
        resultsCache.set(cacheKey, this.collectCards(productIds, limit));
      }

      const result = await resultsCache.get(cacheKey);
      if (!result.complete) resultsCache.delete(cacheKey);
      if (!this.isConnected) return;

      let cards = result.cards;

      if (!cards.length) cards = this.getFallbackCards();
      if (!cards.length) return;

      this.track.innerHTML = cards.join('');
      const isCarousel = cards.length > 1;
      this.classList.toggle('pp-cart-cross-sell--carousel', isCarousel);
      this.querySelector('[data-cross-sell-nav]').hidden = !isCarousel;
      this.hidden = false;
      if (isCarousel) this.initCarousel();
    }

    // Walks the cart products oldest first: the first one with complementary products drives the
    // suggestions, the next ones only fill the remaining slots.
    async collectCards(productIds, limit) {
      const cartProductIds = new Set(productIds);
      const seenProductIds = new Set();
      const cards = [];
      let complete = true;
      const requests = productIds.slice(0, MAX_TRIGGER_PRODUCTS).map((id) => this.fetchCards(id));

      for (const request of requests) {
        const requestCards = await request;
        if (!requestCards) {
          complete = false;
          continue;
        }

        for (const card of requestCards) {
          const productId = card.dataset.productId;
          if (cartProductIds.has(productId) || seenProductIds.has(productId)) continue;

          seenProductIds.add(productId);
          cards.push(card.outerHTML);
          if (cards.length >= limit) return { cards, complete };
        }
      }

      return { cards, complete };
    }

    async fetchCards(productId) {
      const url = `${this.dataset.url}?section_id=cart-cross-sell&product_id=${productId}&limit=${RECOMMENDATIONS_LIMIT}&intent=complementary`;

      try {
        const response = await fetch(url);
        if (!response.ok) return null;

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        return Array.from(html.querySelectorAll('[data-cross-sell-results] [data-cross-sell-card]'));
      } catch (error) {
        return null;
      }
    }

    getFallbackCards() {
      const template = this.querySelector('[data-cross-sell-fallback]');
      if (!template) return [];

      return Array.from(template.content.querySelectorAll('[data-cross-sell-card]'), (card) => card.outerHTML);
    }

    initCarousel() {
      const track = this.track;
      const slides = Array.from(track.querySelectorAll('[data-cross-sell-slide]'));
      const prevButton = this.querySelector('[data-cross-sell-prev]');
      const nextButton = this.querySelector('[data-cross-sell-next]');
      const dragThreshold = 8;
      let isPointerDown = false;
      let isDragging = false;
      let startX = 0;
      let startScrollLeft = 0;
      let pointerId = null;

      const scrollToIndex = (index) => {
        const target = slides[index];
        if (!target) return;
        track.scrollTo({
          left: target.offsetLeft,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      };

      const getActiveIndex = () => {
        let closestIndex = 0;
        let closestDistance = Infinity;

        slides.forEach((slide, index) => {
          const distance = Math.abs(slide.offsetLeft - track.scrollLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      };

      const updateControls = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth - 2);
        prevButton.disabled = track.scrollLeft <= 2;
        nextButton.disabled = track.scrollLeft >= maxScroll;
      };

      const scrollByDirection = (direction) => {
        scrollToIndex(Math.min(slides.length - 1, Math.max(0, getActiveIndex() + direction)));
      };

      const stopDragging = () => {
        if (!isPointerDown) return;

        if (pointerId !== null && track.hasPointerCapture(pointerId)) {
          track.releasePointerCapture(pointerId);
        }

        const wasDragging = isDragging;
        isPointerDown = false;
        isDragging = false;
        pointerId = null;
        track.classList.remove('is-dragging');

        if (wasDragging) {
          scrollToIndex(getActiveIndex());
          updateControls();
        }
      };

      prevButton.addEventListener('click', () => scrollByDirection(-1));
      nextButton.addEventListener('click', () => scrollByDirection(1));

      track.addEventListener('pointerdown', (event) => {
        // Touch and pen use native scrolling (with scroll snap); only mouse needs drag emulation.
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        // Capturing the pointer on a control swallows its click and blocks add to cart.
        if (event.target.closest('a, button, select, input, label, textarea, product-form')) return;

        isPointerDown = true;
        isDragging = false;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScrollLeft = track.scrollLeft;
      });

      track.addEventListener('pointermove', (event) => {
        if (!isPointerDown || event.pointerId !== pointerId) return;

        const deltaX = event.clientX - startX;

        if (!isDragging && Math.abs(deltaX) > dragThreshold) {
          isDragging = true;
          track.classList.add('is-dragging');
          track.setPointerCapture(event.pointerId);
        }

        if (!isDragging) return;

        event.preventDefault();
        track.scrollLeft = startScrollLeft - deltaX;
        updateControls();
      });

      track.addEventListener('pointerup', stopDragging);
      track.addEventListener('pointercancel', stopDragging);
      track.addEventListener('lostpointercapture', stopDragging);
      track.addEventListener('scroll', () => window.requestAnimationFrame(updateControls), { passive: true });

      track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollByDirection(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollByDirection(1);
        }
      });

      updateControls();
    }
  }

  customElements.define('pp-cart-cross-sell', PpCartCrossSell);
}
